import {
  CollectionReference,
  DocumentReference,
  Query,
  Transaction,
} from '@firebase/firestore-types';
import { Users } from '@just-in-types';
import { firestore } from '../firebase';
import {
  cleanBoolean,
  cleanLanguage,
  cleanMobile,
  cleanName,
  cleanString,
  cleanVehicles,
} from './cleaningFunctions';

const checkInsRef = firestore.collection('checkIns');
const usersRef = firestore.collection('users');

const getUserRefs = async ({ mobileToClean }: { mobileToClean: string }) => {
  let ref: CollectionReference | Query = usersRef;

  if (mobileToClean) {
    ref = usersRef.where('mobile', '==', mobileToClean);
  }

  const query = await ref.get();
  const userRefs: DocumentReference[] = [];

  query.forEach(doc => userRefs.push(doc.ref));

  return userRefs;
};

const getCheckIns = async ({ userId }: { userId: string }) => {
  const query = await checkInsRef.where('userId', '==', userId).get();
  const checkIns: Users.CheckIn[] = [];

  query.forEach(doc =>
    checkIns.push({ checkInId: doc.id, ...doc.data() } as Users.CheckIn),
  );

  return checkIns;
};

const updateCheckIns = async ({
  checkIns,
  transaction,
  userRef,
}: {
  checkIns: Users.CheckIn[];
  transaction: Transaction;
  userRef: DocumentReference;
}) => {
  const userCheckInsRef = userRef.collection('checkIns');
  const transactions: Transaction[] = [];

  checkIns.forEach(checkIn =>
    transaction.set(userCheckInsRef.doc(checkIn.checkInId), checkIn, {
      merge: true,
    }),
  );

  const nextTransactions = await Promise.all(transactions);

  return nextTransactions;
};

const cleanUserData = async ({
  transaction,
  userData,
  userId,
  userRef,
}: {
  transaction: Transaction;
  userData: Users.User;
  userId: string;
  userRef: DocumentReference;
}): Promise<{ nextTransaction: Transaction; user: Users.User }> => {
  const checkIns = await getCheckIns({ userId });
  const nextTransactions = await updateCheckIns({
    checkIns,
    transaction,
    userRef,
  });
  const nextTransaction = nextTransactions[0];
  const { mobile, secretMobile } = cleanMobile(userData.mobile);
  const user: Users.User = {
    ...userData,
    hasAccount: cleanBoolean(userData.hasAccount),
    language: cleanLanguage(userData.language),
    mobile,
    mugshot: cleanString(userData.mugshot),
    mugshotUrl: cleanString(userData.mugshotUrl as string),
    name: cleanName(userData.name),
    secretMobile,
    signature: cleanString(userData.signature),
    signatureUrl: cleanString(userData.signatureUrl),
    userId,
    vehicles: userData.vehicles ? cleanVehicles(userData.vehicles) : [],
    zip: userData.zip || null,
  };

  return {
    nextTransaction,
    user,
  };
};

const buildTransaction = (userRef: DocumentReference) =>
  firestore.runTransaction(async transaction => {
    const userId = userRef.id;
    const userDoc = await transaction.get(userRef);

    if (!userDoc.exists) {
      console.error(`userDoc(${userId}) doesn't exist!`);

      return undefined;
    }

    const userData = userDoc.data() as Users.User;

    if (!userData) {
      console.error(`userData(${userId}) is undefined!`);

      return undefined;
    }

    const { user } = await cleanUserData({
      transaction,
      userData,
      userId,
      userRef,
    });

    return transaction.set(userRef, user);
  });

export default async ({ mobileToClean }: { mobileToClean: string }) => {
  const userRefs = await getUserRefs({ mobileToClean });
  const transactions: Array<Promise<undefined | Transaction>> = [];

  for (const userRef of userRefs) {
    transactions.push(buildTransaction(userRef));
  }

  return Promise.all(transactions);
};



// WEBPACK FOOTER //
// ./src/adminFunctions/cleanUsers.ts